import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { store } from "../index";
import ApiHelper from "../Utils/API/apiHelper";

/**
 * Subscribes to real-time setup log entries for a specific FlowBox device.
 * Connects to the FlowBox SignalR hub and joins the device group so the
 * callback fires immediately whenever the device logs a button press in
 * setup mode — no polling required.
 *
 * @param {number|null} deviceId - The device to watch, or null to skip.
 * @param {(entry: object) => void} onNewEntry - Called with each new log entry.
 */
export function useFlowBoxSetupLog(deviceId, onNewEntry) {
  const callbackRef = useRef(onNewEntry);
  useEffect(() => { callbackRef.current = onNewEntry; }, [onNewEntry]);

  useEffect(() => {
    if (!deviceId) return;

    const apiBase = ApiHelper.appSettings?.TAPLR_API_BASE_URL;
    if (!apiBase) return;

    let hubUrl;
    try {
      hubUrl = new URL(apiBase).origin + "/hubs/flowbox";
    } catch {
      return;
    }

    let stopped = false;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => store.getState().Login.userToken,
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("SetupLogEntry", (entry) => {
      callbackRef.current?.(entry);
    });

    connection
      .start()
      .then(() => {
        if (stopped) return;
        return connection.invoke("JoinDevice", deviceId);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          console.warn("[FlowBoxHub] SetupLog connection/join failed:", err);
        }
      });

    return () => {
      stopped = true;
      connection.stop().catch(() => {});
    };
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps
}
